import { backendUrl } from '@jdiyield/constants'
import { AnotherToken, LendContract, Lending, Position, PriceList, Token } from '@jdiyield/interfaces'
import { api } from '@jdiyield/utils'
import BigNumber from 'bignumber.js'
import throat from 'throat'
import Web3 from 'web3'

import { findToken, makeBatchRequest } from '../utils'

export const getAlpacaLending = async (
  web3: Web3,
  walletAddress: string,
  lendContract: LendContract,
  tokens: Token[],
  priceList: PriceList
): Promise<Lending> => {
  try {
    const lendTokens = lendContract?.lendTokens || []
    const { debtShareToken } = await import('@jdiyield/abi')
    const tokenData = await makeBatchRequest(
      web3,
      lendTokens.flatMap((tokenAddress) => {
        const contract = new web3.eth.Contract(debtShareToken as any, tokenAddress)
        return [
          contract.methods.totalSupply().call,
          contract.methods.totalToken().call,
          contract.methods.token().call,
          contract.methods.balanceOf(walletAddress).call,
          contract.methods.nextPositionID().call
        ]
      })
    )

    const chunkTokenData = [...new Array(tokenData.length / 5)].map((_, i) => {
      const index = i * 5
      return {
        ibToken: lendTokens[i],
        totalSupply: tokenData[index],
        totalToken: tokenData[index + 1],
        borrowTokenAddress: tokenData[index + 2],
        balance: tokenData[index + 3],
        nextPositionID: tokenData[index + 4]
      }
    })

    const formatLendInfos = await Promise.allSettled(
      chunkTokenData.map(
        throat(2, async ({ ibToken, borrowTokenAddress, totalSupply, totalToken, balance }) => {
          const ratio = +totalToken / +totalSupply ?? 1
          const usdPerCoin = priceList[borrowTokenAddress.toLowerCase()]?.usd || 0
          const tokenInfo = findToken(tokens, borrowTokenAddress)
          const decimals = tokenInfo?.decimals || 18

          const contract = new web3.eth.Contract(debtShareToken as any, ibToken)

          const { data = [] } = await api.get(`${backendUrl}/positions`, {
            params: {
              contract: ibToken,
              walletAddress
            }
          })
          const positionInfos = await makeBatchRequest(
            web3,
            data.flatMap(({ id }) => [
              contract.methods.positions(`${id}`).call,
              contract.methods.positionInfo(`${id}`).call
            ])
          )
          const chunkPositionInfos = [...new Array(positionInfos.length / 2)].map((_, i) => {
            const index = i * 2
            return {
              positions: positionInfos[index],
              positionInfo: positionInfos[index + 1]
            }
          })

          const allCollateralOnPositions = chunkPositionInfos.reduce((sum, curr) => {
            const {
              positionInfo: { '0': collateralBalance }
            } = curr
            return sum.plus(collateralBalance)
          }, new BigNumber(0))
          //Collateral
          const amount = new BigNumber(balance).multipliedBy(ratio).plus(allCollateralOnPositions)
          const originalBalance = amount
          const collateralToken = {
            logo: tokenInfo?.logo || '',
            symbol: tokenInfo?.symbol || '???',
            name: tokenInfo?.name || '???',
            amount: originalBalance,
            value: originalBalance.dividedBy(`1e${decimals || 18}`).toNumber(),
            decimals,
            address: tokenInfo?.address || borrowTokenAddress,
            valueUSD: originalBalance
              .dividedBy(`1e${decimals || 18}`)
              .multipliedBy(usdPerCoin)
              .toNumber()
          } as AnotherToken

          //Borrowed
          const allBorrowOnPositions = chunkPositionInfos.reduce((sum, curr) => {
            const {
              positionInfo: { '1': debtShare }
            } = curr
            return sum.plus(debtShare)
          }, new BigNumber(0))
          const amountBorrowed = new BigNumber(allBorrowOnPositions)
          const originalBalanceBorrowed = amountBorrowed

          const borrowToken = {
            logo: tokenInfo?.logo || '',
            symbol: tokenInfo?.symbol || '???',
            name: tokenInfo?.name || '???',
            amount: originalBalanceBorrowed,
            value: originalBalanceBorrowed.dividedBy(`1e${decimals || 18}`).toNumber(),
            decimals,
            address: tokenInfo?.address || borrowTokenAddress,
            valueUSD: originalBalanceBorrowed
              .dividedBy(`1e${decimals || 18}`)
              .multipliedBy(usdPerCoin)
              .toNumber()
          } as AnotherToken
          return {
            collateralToken,
            borrowToken
          }
        })
      )
    )
    formatLendInfos
    const collateralTokens: AnotherToken[] = formatLendInfos
      .map((promise) => {
        if (promise.status === 'fulfilled') return promise.value.collateralToken
        return { value: 0, valueUSD: 0 } as AnotherToken
      })
      .filter(({ value }) => value !== 0)
    const borrowTokens: AnotherToken[] = formatLendInfos
      .map((promise) => {
        if (promise.status === 'fulfilled') return promise.value.borrowToken
        return { value: 0, valueUSD: 0 } as AnotherToken
      })
      .filter(({ value }) => value !== 0)

    // const ratio = +totalToken / +totalSupply ?? 1
    const supplyBalance = collateralTokens.reduce((sum, { valueUSD }) => sum + valueUSD, 0)
    const borrowBalance = borrowTokens.reduce((sum, { valueUSD }) => sum + valueUSD, 0)
    return {
      collateralTokens,
      supplyBalance,
      borrowBalance,
      borrowTokens
    }
  } catch (error) {
    console.log(error)
    return {
      collateralTokens: [],
      borrowBalance: 0,
      borrowTokens: [],
      supplyBalance: 0
    }
  }
}

export const getAlpacaPositions = async (web3, contract: string, latestPosition = 0): Promise<Position[]> => {
  const { debtShareToken } = await import('@jdiyield/abi')
  const ct = new web3.eth.Contract(debtShareToken as any, contract)
  const nextPosition = +(await ct.methods.nextPositionID().call())
  const latestIDinDB = latestPosition

  if (nextPosition - 1 == latestIDinDB) {
    return []
  }
  const mustFetchMore = [...new Array(nextPosition - 1 - latestIDinDB)].map((_, i) => latestIDinDB + i + 1)

  if (mustFetchMore.length === 0) return []

  const chunkIndex = 1000
  const chunks = mustFetchMore.reduce((sum, id, index) => {
    if (sum.length === 0) {
      return [[id]]
    }
    if (index % chunkIndex === 0) {
      sum.push([id])
    } else {
      sum[sum.length - 1].push(id)
    }
    return sum
  }, [])

  const result = await Promise.all(
    chunks.flatMap(
      throat(1, async (arr) => {
        const positionInNode = await makeBatchRequest(
          web3,
          arr.map((id) => ct.methods.positions(id).call)
        )
        return positionInNode
          .map(({ owner, debtShare }, index) =>
            +debtShare !== 0
              ? ({
                  owner: owner.toLowerCase(),
                  id: arr[index]
                } as Position)
              : null
          )
          .filter((position) => !!position)
      })
    )
  )
  return result.flat() as Position[]
}
